import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';

export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [counters, setCounters] = useState({
    totalSupply: 0,
    circulating: 0,
    tokenHolders: 0,
    transactions: 0
  });

  // Smooth scroll effect for parallax
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated counters
  useEffect(() => {
    const timer = setTimeout(() => {
      const animateCounter = (start: number, end: number, duration: number, callback: (value: number) => void) => {
        let startTime: number | null = null;
        const step = (timestamp: number) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          const current = Math.floor(progress * (end - start) + start);
          callback(current);
          if (progress < 1) {
            window.requestAnimationFrame(step);
          }
        };
        window.requestAnimationFrame(step);
      };

      animateCounter(0, 100, 2000, (value) => setCounters(prev => ({ ...prev, totalSupply: value })));
      animateCounter(0, 10, 2000, (value) => setCounters(prev => ({ ...prev, circulating: value })));
      animateCounter(0, 5000, 1500, (value) => setCounters(prev => ({ ...prev, tokenHolders: value })));
      animateCounter(0, 10, 2500, (value) => setCounters(prev => ({ ...prev, transactions: value })));
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
              transform: `translateY(${scrollY * 0.5}px)`
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {/* Hero Badges */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <div className="hero-badge px-6 py-3 rounded-full">
              <span className="text-gold font-medium">{t('hero.badge1')}</span>
            </div>
            <div className="hero-badge px-6 py-3 rounded-full">
              <span className="text-white font-medium">{t('hero.badge2')}</span>
            </div>
          </div>

          <h1 className="hero-title text-4xl md:text-6xl lg:text-7xl font-black mb-8 leading-tight">
            <span className="block text-white">{t('hero.title1')}</span>
            <span className="block">{t('hero.title2')}</span>
          </h1>

          <div className="hero-subtitle max-w-5xl mx-auto mb-12">
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              {t('hero.subtitle')}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <Link to="/buy">
              <Button variant="primary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                <span className="relative z-10 flex items-center">
                  <i className="fas fa-shopping-cart mr-3 group-hover:rotate-12 transition-transform"></i>
                  {t('hero.buyButton')}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-indonesia-red to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Button>
            </Link>

            <Link to="/whitepaper">
              <Button variant="secondary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                <span className="relative z-10 flex items-center">
                  <i className="fas fa-book-open mr-3 group-hover:scale-110 transition-transform"></i>
                  {t('hero.whitepaperButton')}
                </span>
              </Button>
            </Link>
          </div>

          {/* Proof Points */}
          <div className="flex flex-wrap gap-8 justify-center text-gray-400">
            <span className="flex items-center gap-3 text-lg">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                <i className="fas fa-check text-white text-xs"></i>
              </div>
              {t('hero.proof1')}
            </span>
            <span className="flex items-center gap-3 text-lg">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                <i className="fas fa-check text-white text-xs"></i>
              </div>
              {t('hero.proof2')}
            </span>
            <span className="flex items-center gap-3 text-lg">
              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                <i className="fas fa-check text-white text-xs"></i>
              </div>
              {t('hero.proof3')}
            </span>
          </div>
        </div>
      </section>

      {/* Strategic Partners Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('partners.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
              {t('partners.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('partners.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="partner-card group">
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-red-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <img src="/IndotanGroup.png" alt="Indotan Group" className="relative h-24 mx-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('partners.indotan.name')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('partners.indotan.description')}
              </p>
            </div>

            <div className="partner-card group">
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-red-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <img src="/PTNHM_FC.png" alt="PT Nusa Halmahera Minerals" className="relative h-24 mx-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('partners.nhm.name')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('partners.nhm.description')}
              </p>
              <a
                href="https://www.nhm.co.id/en/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2"
              >
                {t('partners.nhm.link')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* NFT & Token Metrics Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('metrics.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('metrics.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('metrics.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.totalSupply.toLocaleString()}M
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.totalSupply')}</div>
            </div>

            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.circulating.toLocaleString()}M
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.circulating')}</div>
            </div>

            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.tokenHolders.toLocaleString()}+
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.tokenHolders')}</div>
            </div>

            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.transactions.toLocaleString()}K+
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.transactions')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose IDNGold NFTs Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('whyChoose.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('whyChoose.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('whyChoose.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="modern-card group">
              <div className="feature-icon">
                <i className="fas fa-exchange-alt text-3xl text-gold group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                {t('whyChoose.features.liquidity.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('whyChoose.features.liquidity.description')}
              </p>
            </div>

            <div className="modern-card group">
              <div className="feature-icon">
                <i className="fas fa-coins text-3xl text-gold group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                {t('whyChoose.features.goldConvert.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('whyChoose.features.goldConvert.description')}
              </p>
            </div>

            <div className="modern-card group">
              <div className="feature-icon">
                <i className="fas fa-th text-3xl text-gold group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                {t('whyChoose.features.multipleUtility.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('whyChoose.features.multipleUtility.description')}
              </p>
            </div>

            <div className="modern-card group">
              <div className="feature-icon">
                <i className="fas fa-users text-3xl text-gold group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                {t('whyChoose.features.community.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('whyChoose.features.community.description')}
              </p>
            </div>

            <div className="modern-card group">
              <div className="feature-icon">
                <i className="fas fa-network-wired text-3xl text-gold group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                {t('whyChoose.features.suiNetwork.title')}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {t('whyChoose.features.suiNetwork.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Why IDNGold Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="glass-card">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-6">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('mission.tag1')}</span>
              </div>
              <h3 className="text-3xl font-black text-white mb-6">{t('mission.title1')}</h3>
              <p className="text-gray-400 leading-relaxed text-lg">
                {t('mission.description1')}
              </p>
            </div>

            <div className="glass-card">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-6">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('mission.tag2')}</span>
              </div>
              <h3 className="text-3xl font-black text-white mb-6">{t('mission.title2')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.accessible')}</span>
                </li>
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.liquid')}</span>
                </li>
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.transparent')}</span>
                </li>
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.connected')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('howItWorks.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('howItWorks.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('howItWorks.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="step-card modern-card text-center">
              <div className="step-number">1</div>
              <h3 className="text-xl font-bold text-white mb-4">{t('howItWorks.steps.step1.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('howItWorks.steps.step1.description')}
              </p>
            </div>

            <div className="step-card modern-card text-center">
              <div className="step-number">2</div>
              <h3 className="text-xl font-bold text-white mb-4">{t('howItWorks.steps.step2.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('howItWorks.steps.step2.description')}
              </p>
            </div>

            <div className="step-card modern-card text-center">
              <div className="step-number">3</div>
              <h3 className="text-xl font-bold text-white mb-4">{t('howItWorks.steps.step3.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('howItWorks.steps.step3.description')}
              </p>
            </div>

            <div className="step-card modern-card text-center">
              <div className="step-number">4</div>
              <h3 className="text-xl font-bold text-white mb-4">{t('howItWorks.steps.step4.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('howItWorks.steps.step4.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('useCases.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('useCases.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('useCases.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-chart-line text-4xl text-gold"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('useCases.savings.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('useCases.savings.description')}
              </p>
            </div>

            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-shopping-cart text-4xl text-gold"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('useCases.shopping.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('useCases.shopping.description')}
              </p>
            </div>

            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-chart-line text-4xl text-gold"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('useCases.trading.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('useCases.trading.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Learn/Resources Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('learn.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('learn.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('learn.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="modern-card group">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('learn.whitepaper.tag')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('learn.whitepaper.title')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('learn.whitepaper.description')}
              </p>
              <Link to="/whitepaper" className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2">
                {t('learn.whitepaper.action')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>

            <div className="modern-card group">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('learn.howToBuy.tag')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('learn.howToBuy.title')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('learn.howToBuy.description')}
              </p>
              <Link to="/buy" className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2">
                {t('learn.howToBuy.action')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>

            <div className="modern-card group">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('learn.utilities.tag')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('learn.utilities.title')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('learn.utilities.description')}
              </p>
              <Link to="/utilities" className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2">
                {t('learn.utilities.action')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Supported By Section with Marquee */}
      <section className="section relative bg-bg-secondary/30 overflow-hidden">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-4">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('trustedPartners.eyebrow')}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white/80 mb-3">
              {t('trustedPartners.title')}
            </h2>
            <p className="text-gray-400 text-sm max-w-3xl mx-auto">
              {t('trustedPartners.description')}
            </p>
          </div>

          {/* Marquee Container */}
          <div className="relative">
            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent z-10 pointer-events-none"></div>

            {/* Marquee Track */}
            <div className="flex overflow-hidden">
              <div className="flex animate-marquee gap-16 items-center py-8">
                {/* First Set of Logos */}
                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-komdigi.png"
                    alt="Kementerian Komunikasi dan Digital"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-pse-small.png"
                    alt="PSE Kominfo"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[280px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/IndotanGroup.png"
                    alt="Indotan Group"
                    className="max-h-20 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/PTNHM_FC.png"
                    alt="PT Nusa Halmahera Minerals"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>


                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/xendit.png"
                    alt="Xendit"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/Sui_Symbol_Sea.png"
                    alt="Sui Network"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/cetus.png"
                    alt="Cetus Protocol"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <a
                  href="https://skynet.certik.com/projects/idngold"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/CertiK.png"
                    alt="CertiK"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>

                <a
                  href="https://coinmarketcap.com/currencies/idngold/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/cmc.png"
                    alt="CoinMarketCap"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>

                {/* Duplicate Set for Seamless Loop */}
                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-komdigi.png"
                    alt="Kementerian Komunikasi dan Digital"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-pse-small.png"
                    alt="PSE Kominfo"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[280px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/IndotanGroup.png"
                    alt="Indotan Group"
                    className="max-h-20 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/PTNHM_FC.png"
                    alt="PT Nusa Halmahera Minerals"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>


                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/xendit.png"
                    alt="Xendit"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/Sui_Symbol_Sea.png"
                    alt="Sui Network"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/cetus.png"
                    alt="Cetus Protocol"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <a
                  href="https://skynet.certik.com/projects/idngold"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/CertiK.png"
                    alt="CertiK"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>

                <a
                  href="https://coinmarketcap.com/currencies/idngold/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/cmc.png"
                    alt="CoinMarketCap"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section pb-20">
        <div className="container">
          <div className="cta-section relative">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 relative z-10">{t('finalCta.title')}</h2>
            <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10">
              {t('finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center relative z-10">
              <Link to="/buy">
                <Button variant="primary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                  <span className="relative z-10">{t('finalCta.buyButton')}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-indonesia-red to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="secondary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                  <span className="relative z-10">{t('finalCta.learnButton')}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};